import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { createEnumColumn, isNonNullable } from '@ipfabric/utils';
import type { RowSelectionFilter } from 'components/ApiTable/model/ApiTableModel';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { Button } from 'components/Button/Button';
import { ButtonLink } from 'components/Button/ButtonLink';
import { ModalRightDrawer } from 'components/ModalRightDrawer/ModalRightDrawer';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type {
  TableBulkAction,
  TableBulkActionClickEvent,
  TableRowIdValue,
} from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { DownloadCliLog } from 'containers/DownloadCliLog/DownloadCliLog';
import type { TaskErrorReason } from 'pages/SnapshotManagement/Tasks/TaskErrorReason';
import { discoveryProcessStart } from 'pages/SnapshotManagement/actions';
import { TASKS_STATUSES_DISABLE_REDISCOVERY } from 'pages/SnapshotManagement/constants';
import { useMemo, useState } from 'react';
import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import { exceptionsTypesByKey } from 'translations/exceptions';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { ErrorReasonsDetail } from './ErrorReasonsDetail';

const TABLE_API_ENDPOINT = '/tables/reports/discovery-tasks';

const rowSelectionFilter: RowSelectionFilter = {
  canRowBeSelected: (row) =>
    !TASKS_STATUSES_DISABLE_REDISCOVERY.includes((row as any).status) &&
    ((row as any).ipv4 ?? (row as any).ipv6) !== null,
  selectAllFilter: {
    and: [
      {
        and: TASKS_STATUSES_DISABLE_REDISCOVERY.map((status) => ({
          status: ['neq', status],
        })),
      },
      { or: [{ ipv4: ['empty', false] }, { ipv6: ['empty', false] }] },
    ],
  },
};

type ErrorReasonsDetailRowData = {
  ipv4: string;
  ipv6: string;
  errorReasons: TaskErrorReason[];
};

export const SnapshotManagementTasksPage: FC = () => {
  const tasksMsg = useLocale().snapshotManagementTasks;
  const confirmDialog = useSimpleConfirmDialog();
  const dispatch = useDispatch();
  const notifications = useNotifications();
  const api = useApi();
  const [errorReasonsDetailRow, setErrorReasonsDetailRow] =
    useState<ErrorReasonsDetailRowData | null>(null);

  const endpoint = useTableEndpoint({
    apiEndpoint: TABLE_API_ENDPOINT,
    alternative: (origEndpoint) => ({
      id: origEndpoint.id,
      webDependencies: [
        ...(origEndpoint.webDependencies ?? []),
        'status', // for determining if row can be selected
      ],
      columns: [
        ...origEndpoint.columns.map((column) =>
          column.key === 'errorType'
            ? createEnumColumn({
                // Keep as text in API, create enum column in frontend
                // to keep single source of truth for error types dictionary.
                // Can be done better, e.g. implement dictionaries to OAS,
                // but not worthy the effort now.
                key: 'errorType',
                title: column.title,
                options: exceptionsTypesByKey,
              })
            : column,
        ),
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: tasksMsg.actionColumnTitle,
          webDependencies: [
            'errorReasons',
            'hasLogFile',
            'id',
            'context',
            // load IPs in case user hides IPs and selects row to exclude/rediscover IP
            'ipv4',
            'ipv6',
          ],
          render: (rowData) => (
            <>
              {rowData.hasLogFile && (
                <DownloadCliLog
                  btnText={tasksMsg.btnShowLog}
                  className='mr-2'
                  collection='task'
                  dataKey={rowData.id}
                  fileName={`${(rowData.ipv4 || rowData.ipv6) ?? ''}_${rowData.context ?? ''}_${rowData.id ?? ''}`}
                />
              )}
              {Array.isArray(rowData.errorReasons) &&
                rowData.errorReasons.length > 0 && (
                  <Button
                    variant='secondary'
                    size='xxs'
                    onClick={() => {
                      setErrorReasonsDetailRow(rowData);
                    }}
                    type='button'
                    icon='fa-triangle-exclamation'
                    iconStyle='regular'
                  >
                    {tasksMsg.btnErrors}
                  </Button>
                )}
            </>
          ),
        }),
      ],
    }),
  });

  const loadTableIPsForSelectedRows = useStableCallback(
    async (
      selectedRows: Set<TableRowIdValue>,
    ): Promise<Record<string, any>[]> => {
      const fetchedSelectedRows = await api.request({
        method: 'POST',
        url: TABLE_API_ENDPOINT,
        data: {
          columns: ['ipv4', 'ipv6'],
          snapshot: selectedSnapshotId,
          filters: {
            or: Array.from(selectedRows).map((id) => ({
              id: ['eq', id],
            })),
          },
        },
      });
      return fetchedSelectedRows.data;
    },
  );

  const getRequestIpList = useStableCallback(
    async (
      selectedRows: Set<TableRowIdValue>,
      getIpValue: (row: Record<string, any>) => string,
    ) => {
      const data = apiTablePage.apiTableModel.data as Record<string, any>[];
      const availableSelectedDataRows = data.filter((row) =>
        selectedRows.has(row.id),
      );
      const tableHasIPsForAllRows =
        selectedRows.size === availableSelectedDataRows.length;
      const dataWithIPs = tableHasIPsForAllRows
        ? availableSelectedDataRows
        : await loadTableIPsForSelectedRows(selectedRows);
      return dataWithIPs
        .filter((row) => {
          if (isNonNullable(row.ipv4) || isNonNullable(row.ipv6)) {
            return true;
          }
          stumble('Data row with IPs not found');
          return false;
        })
        .map((row) => getIpValue(row));
    },
  );

  const excludeIpsFromDiscovery = async (
    selectedRows: Set<TableRowIdValue>,
  ) => {
    const requestIPs = await getRequestIpList(
      selectedRows,
      (row: Record<string, any>) =>
        isNonNullable(row.ipv4) ? `${row.ipv4}/32` : `${row.ipv6}/128`,
    );
    await api.request({
      data: requestIPs,
      url: '/settings/networks/exclude',
      method: 'POST',
    });
  };

  const handleExcludeClick = useStableCallback(
    async ({
      selectedRows,
      deselectAll,
      indicateRunningAction,
    }: TableBulkActionClickEvent) => {
      if (await confirmDialog.show(tasksMsg.excludeDialog)) {
        await indicateRunningAction(() =>
          excludeIpsFromDiscovery(selectedRows),
        );
        notifications.showNotification({
          message: tasksMsg.excludeSuccess,
          type: 'success',
        });
        deselectAll();
      }
    },
  );

  const errorReasonsDetailRowIp =
    errorReasonsDetailRow &&
    (errorReasonsDetailRow.ipv4 || errorReasonsDetailRow.ipv6);

  // biome-ignore lint/correctness/useExhaustiveDependencies(confirmDialog.show): stable
  const bulkActions = useMemo(
    (): TableBulkAction[] => [
      {
        key: 'rediscover',
        title: tasksMsg.btnRediscover,
        disableAutoDeselect: true,
        onClick: async ({ selectedRows }: TableBulkActionClickEvent) => {
          if (await confirmDialog.show(tasksMsg.rediscoverDialog)) {
            const ipList = await getRequestIpList(
              selectedRows,
              // TODO: Replace Record<string, any> with proper types from https://ipfabric.atlassian.net/browse/NIM-18210
              (row: Record<string, any>) => row.ipv4 ?? row.ipv6,
            );
            dispatch(
              discoveryProcessStart({
                requestData: {
                  action: 'add',
                  ipList,
                  retryTimedOut: false,
                  vCollectKey: selectedSnapshotId,
                },
                redirect: true,
              }),
            );
          }
        },
      },
      {
        key: 'exclude',
        title: tasksMsg.btnExclude,
        disableAutoDeselect: true,
        accent: 'negative',
        icon: 'fa-trash-can',
        onClick: handleExcludeClick,
      },
    ],
    [tasksMsg],
  );
  const selectedSnapshotId = useSnapshotId();
  const apiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      bulkActions,
      rowSelectionFilter,
      rowIdField: 'id',
      externalSettings: {
        stickyColumns: {
          ipv4: true,
          ipv6: true,
        },
      },
    },
  });

  return (
    <div className='flex flex-col flex-1 gap m'>
      <div>
        <ButtonLink
          variant='secondary'
          to={`/snapshot-management/${selectedSnapshotId ?? ''}`}
        >
          {tasksMsg.btnBack}
        </ButtonLink>
      </div>
      <ApiTablePageView model={apiTablePage} />
      {errorReasonsDetailRow !== null && (
        <ModalRightDrawer
          noContentPadding
          flexContent
          onClose={() => {
            setErrorReasonsDetailRow(null);
          }}
          title={`Error detail${errorReasonsDetailRowIp ? `: ${errorReasonsDetailRowIp}` : ''}`}
        >
          <ErrorReasonsDetail
            errorReasons={errorReasonsDetailRow.errorReasons}
          />
        </ModalRightDrawer>
      )}
      {confirmDialog.render()}
    </div>
  );
};
